'use client';

import { Toast } from 'flowbite-react';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  message: string;
  type: ToastType;
  duration?: number;
}

interface ShowToastParams {
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  showToast: (params: ShowToastParams) => void;
  toast: ToastMessage | null;
  hideToast: () => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(
  undefined
);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const showToast = useCallback(
    ({ message, type, duration = 3000 }: ShowToastParams) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setToast({ message, type });
      setVisible(true);
      timerRef.current = setTimeout(() => {
        setVisible(false);
        // Remove from DOM after fade-out animation
        setTimeout(() => setToast(null), 300);
      }, duration);
    },
    []
  );

  const hideToast = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
    setTimeout(() => setToast(null), 300);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, toast, hideToast }}>
      {children}
      {toast && (
        <Toast
          className={`fixed end-4 top-20 z-50 border border-gray-200 bg-gray-100 text-gray-900 transition-all duration-300 ${
            visible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
          }`}
        >
          <div className="text-sm font-normal">{toast.message}</div>
          <Toast.Toggle onClick={hideToast} />
        </Toast>
      )}
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
